import { Box, Divider, Heading, Tag } from "@chakra-ui/react";
import { LEADER_YEAR, YouthKid } from "common";
import { useEffect, useMemo } from "react";
import { IoMdFemale, IoMdMale } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { useAPI } from "../api/useAPI";
import { useAPIQuery } from "../api/useAPIQuery";
import { Card } from "../utils/Card";
import { TeamBanner } from "../utils/TeamBanner";

interface YouthKidCardProps {
  youthKid: YouthKid;
  selectedYouthKidId?: number | null;
  setSelectedYouthKidId?: (id: number | null) => void;
}

export const YouthKidCard = ({
  youthKid,
  selectedYouthKidId,
  setSelectedYouthKidId,
}: YouthKidCardProps) => {
  const navigate = useNavigate();
  const api = useAPI();
  const { data, state, query } = useAPIQuery({ apiCall: api.teamGet });
  const team = useMemo(() => data?.teams[0] ?? undefined, [data?.teams]);

  // Get team
  useEffect(() => {
    if (youthKid.teamId && state !== "loading" && team === undefined) {
      query({
        id: youthKid.teamId,
      });
    }
  }, [query, state, team, youthKid.teamId]);

  return (
    <Card
      position={"relative"}
      display={"flex"}
      flexDir={"column"}
      alignItems={"flex-start"}
      boxShadow={selectedYouthKidId === youthKid.id ? "outline" : "base"}
      width={"100%"}
      padding={4}
      marginBottom={4}
    >
      <Heading
        fontSize={"large"}
        marginBottom={2}
        cursor={"pointer"}
        onClick={() => {
          if (setSelectedYouthKidId) {
            setSelectedYouthKidId(youthKid.id);
            return;
          }
          navigate(`/youth-kid/${youthKid.id}`);
        }}
      >
        {youthKid.fName} {youthKid.lName}
      </Heading>
      <Box display={"flex"}>
        <Tag
          marginRight={2}
          variant={youthKid.year === LEADER_YEAR ? "solid" : "outline"}
          colorScheme={youthKid.year === LEADER_YEAR ? "teal" : undefined}
        >
          {youthKid.year === LEADER_YEAR ? "Leader" : `Year ${youthKid.year}`}
        </Tag>
        <Tag
          variant={"outline"}
          colorScheme={youthKid.gender === "m" ? "blue" : "pink"}
        >
          {youthKid.gender === "m" ? (
            <>
              <IoMdMale />
              <Box>Male</Box>
            </>
          ) : (
            <>
              <IoMdFemale />
              <Box>Female</Box>
            </>
          )}
        </Tag>
      </Box>
      {team ? (
        <Box position={"absolute"} top={0} right={4}>
          <TeamBanner
            color={team.color}
            iconType={team.iconType}
            iconColor={team.iconColor}
            width={60}
            iconSize={30}
          />
        </Box>
      ) : null}
      <Box display={"flex"} flexDir={"column"} width={"100%"}>
        <Divider marginTop={2} />
        {Object.entries(youthKid.properties).map(([name, value], i) => (
          <Box key={i} display={"flex"} width={"100%"} marginTop={2}>
            <Box opacity={"0.8"} width={"50%"}>
              {name}:
            </Box>
            <Box fontWeight={"bold"} width={"100%"} opacity={"0.8"}>
              {value}
            </Box>
          </Box>
        ))}
      </Box>
    </Card>
  );
};
